import React from "react";
import { cn } from "./cn";

/* u1 Button. Variants map onto the redesign's primary / outline / subtle treatments. `className` is merged, never replaced (see cn.js). Renders a real... */
const VARIANTS = {
  primary:
    "bg-[var(--orange)] text-white border border-[var(--orange)] " +
    // Both background and border move together: shifting only the fill would
    // leave the lighter 1px border ringing a darker button.
    "hover:bg-[var(--orange-hover)] hover:border-[var(--orange-hover)]",
  outline:
    "bg-white text-[var(--ink-2)] border border-[var(--line)] hover:border-[var(--line-2)]",
  // Coral outline — the result card's "View profile" call to action.
  coral:
    "bg-white text-[var(--coral)] border border-[var(--coral)] hover:bg-[var(--coral-soft)]",
  // Solid coral with a glow beneath — the design's .btn-order / .btn-become,
  // the one commitment on a screen. Distinct from `primary`, which is the
  // orange used for ordinary form submits.
  // The `shadow:` hint is load-bearing. `shadow-[var(--x)]` is ambiguous to
  // Tailwind — it reads a bare var() as a shadow *colour* and drops the shadow
  // entirely, with no error. `shadow-[shadow:var(--x)]` names the type.
  coralSolid:
    "bg-[var(--coral)] text-white border border-[var(--coral)] shadow-[shadow:var(--coral-glow)] " +
    "hover:bg-[var(--orange-hover)] hover:border-[var(--orange-hover)]",
  subtle:
    "bg-[var(--slate-2)] text-[var(--ink-2)] border border-transparent hover:bg-[var(--line)]",
  // Borderless — a secondary action ("Back") that must not compete with the
  // primary button it sits next to.
  ghost:
    "bg-transparent text-[var(--ink-2)] border border-transparent hover:bg-[var(--ghost-hover)]",
  // On a navy panel — the Financial Setup bar's "Change method". A white
  // button there would read as the primary action on the page; a translucent
  // one sits at the right level and keeps its contrast on the one background
  // it is used against.
  onDark:
    "bg-white/10 text-white border border-white/25 hover:bg-white/20 hover:border-white/40",
};

const SIZES = {
  sm: "h-9 px-3 text-[13px] gap-1.5",
  md: "h-11 px-4 text-sm gap-2",
};

export const Button = ({
  children,
  variant = "primary",
  size = "md",
  className = "",
  type = "button",
  disabled = false,
  ...rest
}) => (
  <button
    type={type}
    disabled={disabled}
    className={cn(
      "inline-flex items-center justify-center rounded-lg font-semibold font-sans",
      "transition-colors focus-visible:outline-none focus-visible:ring-2",
      "focus-visible:ring-[var(--orange)] focus-visible:ring-offset-2",
      VARIANTS[variant] || VARIANTS.primary,
      SIZES[size] || SIZES.md,
      disabled && "opacity-45 cursor-not-allowed",
      className
    )}
    {...rest}
  >
    {children}
  </button>
);

export default Button;
